import { useEffect, useState } from "react";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Trash, Lock } from "@phosphor-icons/react";

export default function OfficeAdmin() {
  return (
    <AdminShell>
      <div className="p-4 md:p-6 space-y-4" data-testid="office-admin-page">
        <div>
          <h1 className="text-4xl font-black uppercase tracking-widest flex items-center gap-2">
            <Lock weight="fill" className="text-red-500"/> Office Admin
          </h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">
            Real-estate agent directory — brokerage contacts, license tracking, referral agreements & W-9s.
            Team Calendar lives under <b>Team Calendar</b>. Eviction cases live under <b>Portfolio → Evictions & Holdovers</b>.
            Edits require owner-level access.
          </p>
        </div>
        <AgentsTab/>
      </div>
    </AdminShell>
  );
}

function Locked({ show }) {
  if (!show) return null;
  return (
    <div className="p-2 bg-amber-50 border-l-4 border-amber-500 text-[11px] flex items-center gap-2">
      <Lock size={14}/> Read-only — owner-level access required to edit.
    </div>
  );
}

function AgentsTab() {
  const [agents, setAgents] = useState([]);
  const [canEdit, setCanEdit] = useState(false);
  const [show, setShow] = useState(false);
  const load = async () => {
    try { const r = await api.get("/office/agents"); setAgents(r.data.agents || []); setCanEdit(!!r.data.can_edit); }
    catch { toast.error("Load failed"); }
  };
  useEffect(() => { load(); }, []);
  const del = async (id) => {
    if (!window.confirm("Delete agent?")) return;
    try { await api.delete(`/office/agents/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed")); }
  };
  return (
    <div className="space-y-3" data-testid="agents-tab">
      <Locked show={!canEdit}/>
      {canEdit && (
        <button onClick={() => setShow(true)} data-testid="new-agent-btn"
          className="h-10 px-3 bg-emerald-600 text-white font-black uppercase tracking-widest text-[11px] shadow-[3px_3px_0_#0f172a] inline-flex items-center gap-1">
          <Plus size={14}/> Add Agent
        </button>
      )}
      {agents.length === 0 ? (
        <div className="rugged-card p-6 text-center text-sm text-slate-500">No agents yet.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {agents.map(a => (
            <div key={a.id} className="p-3 rugged-card space-y-1" data-testid={`agent-${a.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="font-black text-sm">{a.name} {!a.active && <span className="text-[9px] text-slate-400 uppercase">(inactive)</span>}</div>
                  <div className="text-[10px] text-slate-600">{a.brokerage || "—"}</div>
                  <div className="text-[10px]">
                    {a.email && <a href={`mailto:${a.email}`} className="text-cyan-700 hover:underline">{a.email}</a>}
                    {a.phone && <> · <a href={`tel:${a.phone}`} className="text-cyan-700 hover:underline">{a.phone}</a></>}
                  </div>
                  <div className="text-[10px] text-slate-500">
                    License: {a.license_state || "?"} #{a.license_number || "—"}{a.license_expiry && ` exp ${a.license_expiry}`}
                  </div>
                  {a.documents?.length > 0 && <div className="text-[10px] text-emerald-700 mt-1">{a.documents.length} doc{a.documents.length === 1 ? "" : "s"} on file</div>}
                  {a.notes && <div className="text-[11px] mt-1 text-slate-700">{a.notes}</div>}
                </div>
                {canEdit && (
                  <button onClick={() => del(a.id)} className="h-8 w-8 border-2 border-red-600 text-red-600 hover:bg-red-50 inline-flex items-center justify-center">
                    <Trash size={14}/>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {show && <AgentModal onClose={() => setShow(false)} onSaved={() => { setShow(false); load(); }}/>}
    </div>
  );
}

function AgentModal({ onClose, onSaved }) {
  const [f, setF] = useState({ name: "", brokerage: "", email: "", phone: "", license_number: "", license_state: "PA", license_expiry: "", notes: "", active: true });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.post("/office/agents", f); toast.success("Added"); onSaved(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed")); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-slate-900/60 z-[80] flex items-center justify-center p-4" data-testid="agent-modal">
      <div className="bg-white w-full max-w-lg border-4 border-slate-900 shadow-[6px_6px_0_#0f172a]">
        <div className="p-3 bg-emerald-600 text-white flex items-start justify-between">
          <div className="font-black uppercase tracking-widest">Add Agent</div>
          <button onClick={onClose}><X size={18}/></button>
        </div>
        <div className="p-4 space-y-2">
          <input value={f.name} onChange={e => setF({ ...f, name: e.target.value })} placeholder="Full name *" data-testid="agent-name" className="w-full h-10 px-3 border-2 border-slate-300 text-sm"/>
          <input value={f.brokerage} onChange={e => setF({ ...f, brokerage: e.target.value })} placeholder="Brokerage" className="w-full h-10 px-3 border-2 border-slate-300 text-sm"/>
          <div className="grid grid-cols-2 gap-2">
            <input value={f.email} onChange={e => setF({ ...f, email: e.target.value })} placeholder="Email" className="h-10 px-3 border-2 border-slate-300 text-sm"/>
            <input value={f.phone} onChange={e => setF({ ...f, phone: e.target.value })} placeholder="Phone" className="h-10 px-3 border-2 border-slate-300 text-sm"/>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <input value={f.license_state} onChange={e => setF({ ...f, license_state: e.target.value })} placeholder="State" maxLength={4} className="h-10 px-2 border-2 border-slate-300 text-sm"/>
            <input value={f.license_number} onChange={e => setF({ ...f, license_number: e.target.value })} placeholder="License #" className="h-10 px-3 border-2 border-slate-300 text-sm"/>
            <input value={f.license_expiry} onChange={e => setF({ ...f, license_expiry: e.target.value })} placeholder="Expires YYYY-MM-DD" className="h-10 px-2 border-2 border-slate-300 text-sm"/>
          </div>
          <textarea value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} rows={2} placeholder="Notes" className="w-full p-2 border-2 border-slate-300 text-sm"/>
          <button onClick={save} disabled={busy || !f.name} data-testid="agent-save"
            className="w-full h-11 bg-emerald-600 text-white font-black uppercase tracking-widest text-sm disabled:opacity-50 shadow-[3px_3px_0_#0f172a]">
            {busy ? "Saving…" : "Add Agent"}
          </button>
          <div className="text-[10px] text-slate-500">Upload W-9, referral agreement, license PDF after saving.</div>
        </div>
      </div>
    </div>
  );
}
